import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Phone, ArrowUpRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Logo } from "./Logo";
import { NAV_LINKS, PHONE_TEL } from "@/lib/site";
import { useScrollTo } from "./SmoothScroll";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const scrollTo = useScrollTo();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => (e) => { setOpen(false); scrollTo(id, e); };

  return (
    <header data-testid="site-navbar" className="fixed inset-x-0 top-0 z-50 px-3 pt-3 sm:px-5">
      <nav
        className={`mx-auto flex max-w-7xl items-center justify-between rounded-2xl px-4 py-3 transition-[background-color,border-color,box-shadow] duration-500 sm:px-6 ${
          scrolled ? "glass shadow-[0_20px_60px_-30px_rgba(0,0,0,.9)]" : "border border-transparent"
        }`}
      >
        <Link to="/" onClick={go(0)} data-testid="nav-logo-link" aria-label="Pratap Group home">
          <Logo />
        </Link>
        <ul className="hidden items-center gap-1 lg:flex">
          {NAV_LINKS.map((l) => (
            <li key={l.id}>
              <a
                href={`/#${l.id}`}
                onClick={go(l.id)}
                data-testid={`nav-link-${l.id}`}
                className="group relative px-3 py-2 text-sm text-slate-300 transition-colors hover:text-white"
              >
                {l.label}
                <span className="absolute inset-x-3 -bottom-0.5 h-px origin-left scale-x-0 bg-[var(--gold)] transition-transform duration-300 group-hover:scale-x-100" />
              </a>
            </li>
          ))}
        </ul>
        <div className="flex items-center gap-2">
          <a href={PHONE_TEL} data-testid="nav-call-button" aria-label="Call Pratap Group" className="hidden h-10 w-10 items-center justify-center rounded-full border border-white/10 text-slate-200 transition-colors hover:border-[var(--gold)] hover:text-[var(--gold)] sm:flex">
            <Phone className="h-4 w-4" />
          </a>
          <a
            href="/#contact"
            onClick={go("contact")}
            data-testid="nav-quote-button"
            className="btn-gold btn-shine hidden items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-semibold transition-transform duration-300 hover:-translate-y-0.5 sm:inline-flex"
          >
            Get a Free Quote <ArrowUpRight className="h-4 w-4" />
          </a>
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
            className="glass mx-auto mt-2 max-w-7xl rounded-2xl p-4 lg:hidden"
          >
            {NAV_LINKS.map((l) => (
              <a key={l.id} href={`/#${l.id}`} onClick={go(l.id)} data-testid={`mobile-nav-link-${l.id}`} className="font-display block border-b border-white/5 py-3.5 text-lg text-white last:border-0">
                {l.label}
              </a>
            ))}
            <a href="/#contact" onClick={go("contact")} data-testid="mobile-nav-quote-button" className="btn-gold mt-4 flex items-center justify-center gap-2 rounded-full py-3 font-semibold">
              Get a Free Quote <ArrowUpRight className="h-4 w-4" />
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
